import type { AuthorType, DaNodeStatus, MutationOp } from '@okrapdf/schemas';
import { Context, Data, Effect, Layer } from 'effect';

import {
  SqlExecutor,
  layerMemory as sqlExecutorLayerMemory,
  type SqlBinding,
  type SqlExecutorError,
  type SqlRow,
} from './sql-executor.js';

const TypeId = 'okrapdf/core/persistence/DocumentGraphStore/TypeId' as const;
const ErrorTypeId = 'okrapdf/core/persistence/DocumentGraphStore/DocumentGraphStoreError' as const;

export interface DocumentGraphNode {
  readonly id: string;
  readonly parent_id: string | null;
  readonly type: string;
  readonly label: string | null;
  readonly value: string | null;
  readonly status: DaNodeStatus;
  readonly page_number: number | null;
  readonly confidence: number | null;
  readonly metadata: string | null;
  readonly sort_order?: number;
  readonly bbox?: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
  readonly vendor?: string;
  readonly extraction_seq?: number;
}

export interface DocumentGraphStatus {
  readonly totalNodes: number;
  readonly verifiedNodes: number;
  readonly failedNodes: number;
  readonly pendingNodes: number;
  readonly verificationPercent: number;
  readonly pagesCompleted: number;
  readonly pagesFailed: number;
  readonly pagesRunning: number;
  readonly pagesPending: number;
  readonly pagesTotal: number;
  readonly pagesParsed: number;
  readonly pageCount: number;
  readonly pages_parsed: number;
  readonly pages_failed: number;
  readonly page_count: number;
  readonly pageCoverage: 'unknown' | 'empty' | 'partial' | 'complete';
  readonly page_coverage: 'unknown' | 'empty' | 'partial' | 'complete';
  readonly pageCoveragePercent: number;
  readonly page_coverage_percent: number;
  readonly ledgerPagesTotal: number;
  readonly activeVendor: string | null;
}

export interface DocumentGraphFinding {
  readonly findingId: string;
  readonly nodeId: string;
  readonly ruleId: 'confidence_decay';
  readonly severity: 'warning';
  readonly message: string;
  readonly createdAt: number;
}

export interface DocumentGraphMutationMeta {
  readonly nodeType?: string;
  readonly label?: string;
  readonly pageNum?: number;
  readonly vendor?: string;
}

export interface VendorRawRecord {
  readonly vendorLogSeq: number;
  readonly pageNumber: number | null;
  readonly contentType: string;
  readonly rawResponse: string;
  readonly bytes: number;
  readonly r2StorageKey?: string;
}

export interface CostRecordOptions {
  readonly model?: string;
  readonly inputTokens?: number;
  readonly outputTokens?: number;
  readonly nodeId?: string;
  readonly batchId?: string;
}

export interface InsertNodeResult {
  readonly bboxDropped: boolean;
}

export interface FtsRebuildResult {
  readonly count: number;
  readonly elapsedMs: number;
}

export interface DocumentGraphStore {
  readonly [TypeId]: typeof TypeId;
  readonly queryRows: <Row extends SqlRow = SqlRow>(
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<ReadonlyArray<Row>, DocumentGraphStoreError>;
  readonly countRows: (
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<number, DocumentGraphStoreError>;
  readonly getMetaValue: (key: string) => Effect.Effect<string | null, DocumentGraphStoreError>;
  readonly setMeta: (key: string, value: string) => Effect.Effect<void, DocumentGraphStoreError>;
  readonly insertNode: (
    node: DocumentGraphNode,
    searchEnabled: boolean,
  ) => Effect.Effect<InsertNodeResult, DocumentGraphStoreError>;
  readonly insertEdge: (
    sourceId: string,
    targetId: string,
    relation: string,
  ) => Effect.Effect<void, DocumentGraphStoreError>;
  readonly appendDocLog: (
    event: string,
    actorType: string,
    actorId: string | null,
    targetId: string | null,
    detail: Record<string, unknown> | null,
  ) => Effect.Effect<void, DocumentGraphStoreError>;
  readonly logMutation: (
    nodeId: string,
    op: MutationOp,
    authorType: AuthorType,
    authorId: string,
    prevValue: string | null,
    nextValue: string | null,
    meta?: DocumentGraphMutationMeta,
  ) => Effect.Effect<DocumentGraphFinding | undefined, DocumentGraphStoreError>;
  readonly computeStatus: () => Effect.Effect<DocumentGraphStatus, DocumentGraphStoreError>;
  readonly appendLog: (level: string, message: string) => Effect.Effect<void, DocumentGraphStoreError>;
  readonly getStateFingerprint: () => Effect.Effect<string, DocumentGraphStoreError>;
  readonly rebuildFtsIndex: () => Effect.Effect<FtsRebuildResult, DocumentGraphStoreError>;
  readonly storeVendorRaw: (record: VendorRawRecord) => Effect.Effect<number, DocumentGraphStoreError>;
  readonly recordCost: (
    operation: string,
    vendor: string,
    costUsd: number,
    options?: CostRecordOptions,
  ) => Effect.Effect<void, DocumentGraphStoreError>;
}

export type DocumentGraphStoreMethod =
  | 'queryRows'
  | 'countRows'
  | 'getMetaValue'
  | 'setMeta'
  | 'insertNode'
  | 'insertEdge'
  | 'appendDocLog'
  | 'logMutation'
  | 'computeStatus'
  | 'appendLog'
  | 'getStateFingerprint'
  | 'rebuildFtsIndex'
  | 'storeVendorRaw'
  | 'recordCost';

export class DocumentGraphStoreError extends Data.TaggedError('DocumentGraphStoreError')<{
  readonly method: DocumentGraphStoreMethod;
  readonly message: string;
  readonly cause?: unknown;
}> {
  readonly [ErrorTypeId]: typeof ErrorTypeId = ErrorTypeId;
}

export const DocumentGraphStore: Context.Service<DocumentGraphStore, DocumentGraphStore> =
  Context.Service('okrapdf/core/persistence/DocumentGraphStore');

export interface DocumentGraphStoreMakeOptions {
  readonly sql: SqlExecutor;
  readonly now?: () => number;
  readonly randomUuid?: () => string;
}

const SHA256_CONSTANTS = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4,
  0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe,
  0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f,
  0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
  0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116,
  0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
]);

function rotateRight(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

function sha256Hex(input: string): string {
  const source = new TextEncoder().encode(input);
  const paddedLength = Math.ceil((source.byteLength + 9) / 64) * 64;
  const bytes = new Uint8Array(paddedLength);
  bytes.set(source);
  bytes[source.byteLength] = 0x80;
  const bitLength = source.byteLength * 8;
  const view = new DataView(bytes.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);

  const hash = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const words = new Uint32Array(64);

  for (let offset = 0; offset < bytes.byteLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      words[index] = view.getUint32(offset + index * 4, false);
    }
    for (let index = 16; index < 64; index += 1) {
      const previous15 = words[index - 15] ?? 0;
      const previous2 = words[index - 2] ?? 0;
      const sigma0 = rotateRight(previous15, 7) ^ rotateRight(previous15, 18) ^ (previous15 >>> 3);
      const sigma1 = rotateRight(previous2, 17) ^ rotateRight(previous2, 19) ^ (previous2 >>> 10);
      words[index] = ((words[index - 16] ?? 0) + sigma0 + (words[index - 7] ?? 0) + sigma1) >>> 0;
    }

    let a = hash[0] ?? 0;
    let b = hash[1] ?? 0;
    let c = hash[2] ?? 0;
    let d = hash[3] ?? 0;
    let e = hash[4] ?? 0;
    let f = hash[5] ?? 0;
    let g = hash[6] ?? 0;
    let h = hash[7] ?? 0;

    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + sum1 + choice + (SHA256_CONSTANTS[index] ?? 0) + (words[index] ?? 0)) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    hash[0] = ((hash[0] ?? 0) + a) >>> 0;
    hash[1] = ((hash[1] ?? 0) + b) >>> 0;
    hash[2] = ((hash[2] ?? 0) + c) >>> 0;
    hash[3] = ((hash[3] ?? 0) + d) >>> 0;
    hash[4] = ((hash[4] ?? 0) + e) >>> 0;
    hash[5] = ((hash[5] ?? 0) + f) >>> 0;
    hash[6] = ((hash[6] ?? 0) + g) >>> 0;
    hash[7] = ((hash[7] ?? 0) + h) >>> 0;
  }

  return [...hash].map((value) => value.toString(16).padStart(8, '0')).join('');
}

function canonicalize(value: unknown): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(canonicalize);

  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(value).sort()) {
    sorted[key] = canonicalize((value as Record<string, unknown>)[key]);
  }
  return sorted;
}

function digestObject(value: unknown): string {
  return sha256Hex(JSON.stringify(canonicalize(value)));
}

function storeError(method: DocumentGraphStoreMethod, cause: SqlExecutorError): DocumentGraphStoreError {
  return new DocumentGraphStoreError({
    method,
    message: `Unable to execute DocumentGraphStore.${method}`,
    cause,
  });
}

/** Construct a DocumentGraphStore from the single backing SqlExecutor port. */
export function make(options: DocumentGraphStoreMakeOptions): DocumentGraphStore {
  const now = options.now ?? Date.now;
  const randomUuid = options.randomUuid ?? (() => crypto.randomUUID());

  const exec = <Row extends SqlRow = SqlRow>(
    method: DocumentGraphStoreMethod,
    query: string,
    ...bindings: SqlBinding[]
  ): Effect.Effect<ReadonlyArray<Row>, DocumentGraphStoreError> =>
    Effect.mapError(options.sql.exec<Row>(query, ...bindings), (cause) => storeError(method, cause));

  const count = (
    method: DocumentGraphStoreMethod,
    query: string,
    ...bindings: SqlBinding[]
  ): Effect.Effect<number, DocumentGraphStoreError> =>
    Effect.map(exec<{ readonly cnt: number }>(method, query, ...bindings), (rows) => rows[0]?.cnt ?? 0);

  const getMeta = (
    method: DocumentGraphStoreMethod,
    key: string,
  ): Effect.Effect<string | null, DocumentGraphStoreError> =>
    Effect.map(
      exec<{ readonly value: string }>(method, 'SELECT value FROM meta WHERE key = ?', key),
      (rows) => rows[0]?.value ?? null,
    );

  const appendDocumentLog = (
    method: DocumentGraphStoreMethod,
    event: string,
    actorType: string,
    actorId: string | null,
    targetId: string | null,
    detail: Record<string, unknown> | null,
  ): Effect.Effect<void, DocumentGraphStoreError> =>
    Effect.gen(function*() {
      const detailJson = detail ? JSON.stringify(detail) : null;
      const headRows = yield* exec<{ readonly chain_hash: string }>(
        method,
        'SELECT chain_hash FROM document_log WHERE chain_hash IS NOT NULL ORDER BY seq DESC LIMIT 1',
      );
      const prevHash = headRows[0]?.chain_hash ?? null;
      const createdAt = now();
      const chainHash = digestObject({
        prevHash,
        event,
        actorType,
        actorId,
        targetId,
        detail: detailJson,
        createdAt,
      });

      yield* exec(
        method,
        `INSERT INTO document_log (event, actor_type, actor_id, target_id, detail, prev_hash, chain_hash, created_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        event,
        actorType,
        actorId,
        targetId,
        detailJson,
        prevHash,
        chainHash,
        createdAt,
      );
    });

  const queryRows: DocumentGraphStore['queryRows'] = (query, ...bindings) =>
    exec('queryRows', query, ...bindings);

  return DocumentGraphStore.of({
    [TypeId]: TypeId,
    queryRows,
    countRows: (query, ...bindings) => count('countRows', query, ...bindings),
    getMetaValue: (key) => getMeta('getMetaValue', key),
    setMeta: (key, value) =>
      Effect.asVoid(exec('setMeta', 'INSERT OR REPLACE INTO meta (key, value) VALUES (?, ?)', key, value)),
    insertNode: (input, searchEnabled) =>
      Effect.gen(function*() {
        let node = input;
        let bboxDropped = false;
        if (node.bbox) {
          const { x, y, w, h } = node.bbox;
          if (x > 1 || y > 1 || w > 1 || h > 1 || x < 0 || y < 0) {
            node = { ...node, bbox: undefined };
            bboxDropped = true;
          }
        }

        const timestamp = now();
        yield* exec(
          'insertNode',
          `INSERT OR REPLACE INTO nodes (id, parent_id, type, label, value, status, bbox_x, bbox_y, bbox_w, bbox_h, page_number, confidence, metadata, sort_order, vendor, extraction_seq, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          node.id,
          node.parent_id,
          node.type,
          node.label,
          node.value,
          node.status,
          node.bbox?.x ?? null,
          node.bbox?.y ?? null,
          node.bbox?.w ?? null,
          node.bbox?.h ?? null,
          node.page_number,
          node.confidence,
          node.metadata,
          node.sort_order ?? 0,
          node.vendor ?? null,
          node.extraction_seq ?? 0,
          timestamp,
          timestamp,
        );

        if (searchEnabled && (node.label || node.value)) {
          yield* exec('insertNode', 'DELETE FROM nodes_fts WHERE node_id = ?', node.id);
          yield* exec(
            'insertNode',
            `INSERT INTO nodes_fts (node_id, label, value, type)
             SELECT id, label, value, type FROM nodes WHERE id = ?`,
            node.id,
          );
        }

        return { bboxDropped };
      }),
    insertEdge: (sourceId, targetId, relation) =>
      Effect.asVoid(
        exec(
          'insertEdge',
          `INSERT INTO edges (id, source_id, target_id, relation, metadata, created_at)
           VALUES (?, ?, ?, ?, NULL, ?)`,
          randomUuid(),
          sourceId,
          targetId,
          relation,
          now(),
        ),
      ),
    appendDocLog: (event, actorType, actorId, targetId, detail) =>
      appendDocumentLog('appendDocLog', event, actorType, actorId, targetId, detail),
    logMutation: (nodeId, op, authorType, authorId, prevValue, nextValue, meta) =>
      Effect.gen(function*() {
        yield* appendDocumentLog('logMutation', 'mutation', authorType, authorId, nodeId, {
          op,
          prev: prevValue,
          next: nextValue,
          ...meta,
        });

        if (authorType !== 'human' || (op !== 'update' && op !== 'reject')) return undefined;

        yield* exec(
          'logMutation',
          "UPDATE nodes SET confidence = NULL, status = 'needs_review', updated_at = ? WHERE id = ?",
          now(),
          nodeId,
        );
        const findingId = randomUuid();
        const createdAt = now();
        const message = 'Confidence reset after human edit — node requires re-verification';
        yield* exec(
          'logMutation',
          `INSERT INTO findings (id, node_id, rule_id, severity, message, metadata, dismissed_at, created_at)
           VALUES (?, ?, 'confidence_decay', 'warning', 'Confidence reset after human edit — node requires re-verification', NULL, NULL, ?)`,
          findingId,
          nodeId,
          createdAt,
        );
        return {
          findingId,
          nodeId,
          ruleId: 'confidence_decay' as const,
          severity: 'warning' as const,
          message,
          createdAt,
        };
      }),
    computeStatus: () =>
      Effect.gen(function*() {
        const totalNodes = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM nodes WHERE type != 'document' AND type != 'page'",
        );
        const verifiedNodes = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM nodes WHERE status = 'verified' AND type != 'document' AND type != 'page'",
        );
        const failedNodes = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM nodes WHERE status = 'failed' AND type != 'document' AND type != 'page'",
        );
        const pendingNodes = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM nodes WHERE status IN ('needs_review', 'pending') AND type != 'document' AND type != 'page'",
        );
        const ledgerPagesTotal = yield* count('computeStatus', 'SELECT COUNT(*) as cnt FROM page_ledger');
        const pagesCompleted = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM page_ledger WHERE status = 'completed'",
        );
        const pagesRunning = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM page_ledger WHERE status = 'running'",
        );
        const pagesFailed = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM page_ledger WHERE status = 'failed'",
        );
        const ledgerPagesPending = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM page_ledger WHERE status = 'pending'",
        );
        const pageNodesTotal = yield* count(
          'computeStatus',
          "SELECT COUNT(*) as cnt FROM nodes WHERE type = 'page'",
        );
        const totalPagesMeta = yield* getMeta('computeStatus', 'total_pages');
        const totalPagesFromMeta = totalPagesMeta ? parseInt(totalPagesMeta, 10) : 0;
        const pageCount = Number.isFinite(totalPagesFromMeta) && totalPagesFromMeta > 0
          ? totalPagesFromMeta
          : Math.max(ledgerPagesTotal, pageNodesTotal);
        const pagesTotal = pageCount > 0 ? pageCount : ledgerPagesTotal;
        const pagesPending = pagesTotal > 0
          ? Math.max(0, pagesTotal - pagesCompleted - pagesRunning - pagesFailed)
          : ledgerPagesPending;
        const pagesParsed = pagesCompleted;
        const pageCoverage: DocumentGraphStatus['pageCoverage'] = pageCount <= 0
          ? 'unknown'
          : pagesParsed >= pageCount
            ? 'complete'
            : pagesParsed > 0
              ? 'partial'
              : 'empty';
        const pageCoveragePercent = pageCount > 0
          ? Math.round((Math.min(pagesParsed, pageCount) / pageCount) * 100)
          : 0;
        const activeVendor = yield* getMeta('computeStatus', 'pass1_vendor');

        return {
          totalNodes,
          verifiedNodes,
          failedNodes,
          pendingNodes,
          verificationPercent: totalNodes > 0 ? Math.round((verifiedNodes / totalNodes) * 100) : 0,
          pagesCompleted,
          pagesFailed,
          pagesRunning,
          pagesPending,
          pagesTotal,
          pagesParsed,
          pageCount,
          pages_parsed: pagesParsed,
          pages_failed: pagesFailed,
          page_count: pageCount,
          pageCoverage,
          page_coverage: pageCoverage,
          pageCoveragePercent,
          page_coverage_percent: pageCoveragePercent,
          ledgerPagesTotal,
          activeVendor,
        };
      }),
    appendLog: (level, message) =>
      appendDocumentLog('appendLog', 'log', 'system', level, null, { message }),
    getStateFingerprint: () =>
      Effect.gen(function*() {
        const vendorRows = yield* exec<{ readonly chain_hash: string }>(
          'getStateFingerprint',
          'SELECT chain_hash FROM vendor_log WHERE chain_hash IS NOT NULL ORDER BY seq DESC LIMIT 1',
        );
        const documentRows = yield* exec<{ readonly chain_hash: string }>(
          'getStateFingerprint',
          'SELECT chain_hash FROM document_log WHERE chain_hash IS NOT NULL ORDER BY seq DESC LIMIT 1',
        );
        return digestObject(`${vendorRows[0]?.chain_hash ?? ''}${documentRows[0]?.chain_hash ?? ''}`);
      }),
    rebuildFtsIndex: () =>
      Effect.gen(function*() {
        const startedAt = now();
        yield* exec('rebuildFtsIndex', 'DELETE FROM nodes_fts');
        yield* exec(
          'rebuildFtsIndex',
          `INSERT INTO nodes_fts (node_id, label, value, type)
           SELECT id, label, value, type FROM nodes
           WHERE label IS NOT NULL OR value IS NOT NULL`,
        );
        const rebuilt = yield* count(
          'rebuildFtsIndex',
          'SELECT COUNT(*) as cnt FROM nodes_fts',
        );
        return { count: rebuilt, elapsedMs: now() - startedAt };
      }),
    storeVendorRaw: (record) =>
      Effect.gen(function*() {
        if (record.r2StorageKey) {
          yield* exec(
            'storeVendorRaw',
            `INSERT INTO vendor_raw (vendor_log_seq, page_number, content_type, raw_response, bytes, r2_storage_key, created_at)
             VALUES (?, ?, ?, ?, ?, ?, ?)`,
            record.vendorLogSeq,
            record.pageNumber,
            record.contentType,
            record.rawResponse,
            record.bytes,
            record.r2StorageKey,
            now(),
          );
        } else {
          yield* exec(
            'storeVendorRaw',
            `INSERT INTO vendor_raw (vendor_log_seq, page_number, content_type, raw_response, bytes, created_at)
             VALUES (?, ?, ?, ?, ?, ?)`,
            record.vendorLogSeq,
            record.pageNumber,
            record.contentType,
            record.rawResponse,
            record.bytes,
            now(),
          );
        }
        const rows = yield* exec<{ readonly seq: number }>(
          'storeVendorRaw',
          'SELECT last_insert_rowid() as seq',
        );
        return rows[0]?.seq ?? -1;
      }),
    recordCost: (operation, vendor, costUsd, recordOptions) =>
      Effect.gen(function*() {
        const generation = parseInt((yield* getMeta('recordCost', 'reparse_generation')) || '0', 10);
        yield* appendDocumentLog(
          'recordCost',
          'cost',
          'system',
          vendor,
          recordOptions?.nodeId || null,
          {
            operation,
            vendor,
            model: recordOptions?.model || null,
            inputTokens: recordOptions?.inputTokens || null,
            outputTokens: recordOptions?.outputTokens || null,
            costUsd,
            batchId: recordOptions?.batchId || null,
            generation,
          },
        );
      }),
  });
}

/** DocumentGraphStore domain layer over the runtime-provided SqlExecutor. */
export const layer: Layer.Layer<DocumentGraphStore, never, SqlExecutor> = Layer.effect(
  DocumentGraphStore,
  Effect.map(SqlExecutor, (sql) => make({ sql })),
);

/** Process-local DocumentGraphStore composed with the core memory SqlExecutor. */
export const layerMemory: Layer.Layer<DocumentGraphStore> = layer.pipe(
  Layer.provide(sqlExecutorLayerMemory),
);
